import { useMemo, useState } from 'react';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { tieneRespuesta, useEncuesta, useResponder, type RespuestaDada } from './api';
import { PreguntaDelFormulario } from './preguntas';

/**
 * Responder — el formulario de una encuesta, en `#encuestas/<id>`.
 *
 * ## Lo que cambia
 *
 * - **Las preguntas obligatorias se comprueban.** Ni la pantalla ni el servidor
 *   lo hacían: una encuesta entera se podía enviar en blanco y quedaba
 *   registrada como participación. El asterisco rojo no significaba nada.
 * - **Enviar ya no se puede pulsar dos veces.** El servidor rechaza la segunda
 *   —una respuesta por persona—, así que quien respondía veía un error rojo
 *   encima de su «gracias».
 * - Se entra por la sub-ruta, que es la que usa el muro, y «Volver» es un
 *   enlace: el Atrás del navegador vuelve a la lista en vez de salirse del
 *   módulo.
 */
export function Responder({ id }: { id: number }) {
  const consulta = useEncuesta(id);
  const responder = useResponder();
  const [respuestas, setRespuestas] = useState<Record<number, string | string[]>>({});
  const [faltan, setFaltan] = useState<number[]>([]);
  const [enviada, setEnviada] = useState(false);

  const d = consulta.data;
  const preguntas = useMemo(
    () => [...(d?.questions ?? [])].sort((a, b) => a.order - b.order),
    [d],
  );

  function responderUna(idPregunta: number, valor: string | string[]) {
    setRespuestas((r) => ({ ...r, [idPregunta]: valor }));
    // El aviso de «falta» se retira en cuanto se contesta, no al volver a
    // enviar: dejarlo puesto mientras ya está respondida es decir algo falso.
    setFaltan((f) => (f.includes(idPregunta) ? f.filter((x) => x !== idPregunta) : f));
  }

  function enviar() {
    const sinResponder = preguntas
      .filter((q) => q.is_required && !tieneRespuesta(respuestas[q.id]))
      .map((q) => q.id);
    setFaltan(sinResponder);
    if (sinResponder.length) {
      // Y se va a la primera: el aviso vive arriba y la pregunta puede estar
      // tres pantallas más abajo.
      const nodo = document.getElementById(`pregunta-${sinResponder[0]}`);
      nodo?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      nodo?.querySelector<HTMLElement>('input, textarea')?.focus();
      return;
    }
    const cuerpo: RespuestaDada[] = preguntas
      .filter((q) => tieneRespuesta(respuestas[q.id]))
      .map((q) => ({ question_id: q.id, answer_value: respuestas[q.id]! }));
    responder.mutate({ id, respuestas: cuerpo }, {
      onSuccess: () => setEnviada(true),
      onError: (err) =>
        Utils.toast(err instanceof Error ? err.message : 'No se pudo enviar', 'error'),
    });
  }

  if (consulta.isPending) {
    return (
      <div className="mx-auto max-w-2xl" aria-busy="true">
        <Skeleton className="mb-4 h-8 w-32" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }
  if (consulta.isError) {
    return (
      <EstadoError
        err={consulta.error}
        que="la encuesta"
        onReintentar={() => void consulta.refetch()}
        onVolver={() => { location.hash = '#encuestas'; }}
      />
    );
  }
  if (!d) return null;

  if (enviada) return <Gracias anonima={d.is_anonymous} />;

  if (d.already_responded) {
    return (
      <Aviso
        icono="bi-check-circle-fill"
        titulo="Ya respondiste esta encuesta"
        texto="Solo se permite una respuesta por persona. Gracias por participar."
      />
    );
  }

  if (!preguntas.length) {
    return (
      <div className="mx-auto max-w-2xl">
        <Volver />
        <EstadoVacio
          icono="bi-clipboard-check"
          titulo={d.title}
          descripcion="Esta encuesta todavía no tiene preguntas."
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <Volver />
      <Card>
        <header className="border-b border-line px-5 py-4">
          <h1 className="flex items-center gap-2 text-5 font-bold text-brand-ink">
            <i className="bi bi-clipboard-check" aria-hidden="true" /> {d.title}
          </h1>
          {d.description && <p className="mt-1 text-3 text-muted">{d.description}</p>}
          <p className="mt-2 flex items-center gap-1.5 text-2 text-muted">
            <i className={d.is_anonymous ? 'bi bi-shield-lock' : 'bi bi-person-vcard'} aria-hidden="true" />
            {d.is_anonymous
              ? 'Anónima: tu respuesta no se vincula con tu nombre.'
              : 'Identificada: tu respuesta queda a tu nombre.'}
          </p>
        </header>

        <div className="flex flex-col gap-6 px-5 py-5">
          {faltan.length > 0 && (
            <p
              role="alert"
              className="flex items-center gap-2 rounded-sm border border-danger/40 bg-danger/8 px-3 py-2 text-3 font-semibold text-danger-ink"
            >
              <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
              {faltan.length === 1
                ? 'Falta una pregunta obligatoria.'
                : `Faltan ${faltan.length} preguntas obligatorias.`}
            </p>
          )}

          {preguntas.map((q, i) => (
            <PreguntaDelFormulario
              key={q.id}
              pregunta={q}
              numero={i + 1}
              valor={respuestas[q.id]}
              onCambio={(v) => responderUna(q.id, v)}
              falta={faltan.includes(q.id)}
            />
          ))}
        </div>

        <footer className="border-t border-line px-5 py-4">
          <Button block loading={responder.isPending} onClick={enviar}>
            <i className="bi bi-send-fill" aria-hidden="true" /> Enviar mis respuestas
          </Button>
        </footer>
      </Card>
    </div>
  );
}

function Volver() {
  return (
    <a
      href="#encuestas"
      className="mb-4 inline-flex items-center gap-1 text-3 font-semibold text-brand-ink hover:underline focus-visible:focus-anillo"
    >
      <i className="bi bi-arrow-left" aria-hidden="true" /> Volver a encuestas
    </a>
  );
}

function Aviso({ icono, titulo, texto }: { icono: string; titulo: string; texto: string }) {
  return (
    <div className="mx-auto mt-10 max-w-md text-center">
      <p className="text-10 leading-none text-ok-ink">
        <i className={`bi ${icono}`} aria-hidden="true" />
      </p>
      <h1 className="mt-4 text-6 font-bold text-brand-ink">{titulo}</h1>
      <p className="mt-2 text-3 text-muted">{texto}</p>
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <Button variant="secondary" onClick={() => { location.hash = '#encuestas'; }}>
          <i className="bi bi-clipboard-check" aria-hidden="true" /> Ver más encuestas
        </Button>
        <Button onClick={() => { location.hash = '#dashboard'; }}>
          <i className="bi bi-house" aria-hidden="true" /> Volver al inicio
        </Button>
      </div>
    </div>
  );
}

function Gracias({ anonima }: { anonima: boolean }) {
  return (
    <Aviso
      icono="bi-check-circle-fill"
      titulo="¡Gracias por tu respuesta!"
      texto={anonima
        ? 'Quedó registrada de forma anónima. Tu opinión ayuda a mejorar el bienestar del equipo.'
        : 'Quedó registrada a tu nombre. Tu opinión ayuda a mejorar el bienestar del equipo.'}
    />
  );
}
